import React from 'react';
import { Link } from '@mui/material';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkBreaks from 'remark-breaks';
import { MessageProps } from '../../../types/chat';
import MessageBubble from './MessageBubble';

const TextMessage: React.FC<MessageProps> = React.memo(({ 
  message, 
  isCurrentUser,
  onImagePress,
  onReactionPress,
  onReplyPress,
  onDeletePress,
  currentUserId,
  currentUserType
}) => {
  const handleReactionPress = (emoji: string) => {
    onReactionPress?.(message, emoji);
  };

  return (
    <MessageBubble 
      message={message} 
      isCurrentUser={isCurrentUser}
      onReactionPress={handleReactionPress}
      onReplyPress={onReplyPress}
      onDeletePress={onDeletePress}
      onImagePress={onImagePress}
      currentUserId={currentUserId}
      currentUserType={currentUserType}
    >
      <ReactMarkdown
        remarkPlugins={[remarkGfm, remarkBreaks]}
        components={{
          a: ({ href, children, ...props }) => (
            <Link 
              href={href} 
              target="_blank" 
              rel="noopener noreferrer"
              sx={{ 
                color: isCurrentUser ? '#E6F2FF' : '#2563EB',
                textDecoration: 'underline',
                '&:hover': {
                  textDecoration: 'none'
                }
              }}
              {...props}
            >
              {children}
            </Link>
          ),
          p: ({ children }) => (
            <span style={{ 
              margin: 0, 
              lineHeight: 1.4,
              fontSize: '14px',
              fontFamily: 'inherit'
            }}>
              {children}
            </span>
          ),
          // Handle ordered lists
          ol: ({ children }) => (
            <ol style={{
              margin: '8px 0',
              paddingLeft: '20px',
              fontSize: '14px',
              fontFamily: 'inherit'
            }}>
              {children}
            </ol>
          ),
          // Handle unordered lists
          ul: ({ children }) => (
            <ul style={{
              margin: '8px 0',
              paddingLeft: '20px',
              fontSize: '14px',
              fontFamily: 'inherit'
            }}>
              {children}
            </ul>
          ),
          // Handle list items
          li: ({ children }) => (
            <li style={{
              margin: '2px 0',
              lineHeight: 1.4,
              fontSize: '14px',
              fontFamily: 'inherit'
            }}>
              {children}
            </li>
          ),
          // Handle line breaks
          br: () => <br />,
          // Handle bold text
          strong: ({ children }) => (
            <strong style={{ fontWeight: 600 }}>
              {children}
            </strong>
          ),
          // Handle italic text
          em: ({ children }) => (
            <em style={{ fontStyle: 'italic' }}>
              {children}
            </em>
          ),
          // Handle code snippets
          code: ({ children }) => (
            <code style={{
              backgroundColor: isCurrentUser ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.1)',
              padding: '2px 4px',
              borderRadius: '3px',
              fontSize: '13px',
              fontFamily: 'Monaco, Consolas, "Courier New", monospace'
            }}>
              {children}
            </code>
          )
        }}
      >
        {message.message}
      </ReactMarkdown>
    </MessageBubble>
  );
});

TextMessage.displayName = 'TextMessage';

export default TextMessage;
